import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { request } from "../../../utils/request";

// Create async thunk for fetching agents
export const fetchAgents = createAsyncThunk(
  "agents/fetchAgents",
  async (page = 1, { rejectWithValue, getState }) => {
    try {
      // Get current state to validate page before making request
      const state = getState();
      const { agentsCount } = state.agents;
      const agentsPerPage = 10; // Should match the component value
      const totalPages = Math.ceil(agentsCount / agentsPerPage);

      // If requesting a page that doesn't exist and we have agents, redirect to last valid page
      if (page > totalPages && totalPages > 0) {
        const data = await request(
          `${process.env.REACT_APP_API_URL}/agents/?page=${totalPages}`,
          { method: "GET" }
        );
        return { ...data, redirectedFromPage: page, actualPage: totalPages };
      }

      const data = await request(
        `${process.env.REACT_APP_API_URL}/agents/?page=${page}`,
        { method: "GET" }
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch agents");
    }
  }
);

// Create async thunk for uploading agent image
export const uploadAgentImage = createAsyncThunk(
  "agents/uploadAgentImage",
  async ({ imageData, fieldName }, { rejectWithValue }) => {
    try {
      const response = await request(
        `${process.env.REACT_APP_API_URL}/image/`,
        {
          method: "POST",
          body: imageData, // FormData with image
          headers: {}, // Empty headers - request utility will handle FormData properly
        }
      );

      return { response, fieldName }; // Return both response and field name
    } catch (error) {
      return rejectWithValue({
        error: error || "Failed to upload image. Please try again.",
        fieldName,
      });
    }
  }
);

// Create async thunk for creating new agent
export const createAgent = createAsyncThunk(
  "agents/createAgent",
  async (agentData, { rejectWithValue }) => {
    try {
      const data = await request(`${process.env.REACT_APP_API_URL}/agents/`, {
        method: "POST",
        body: JSON.stringify(agentData),
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.error || "Failed to create agent");
    }
  }
);

// Create async thunk for updating agent
export const updateAgent = createAsyncThunk(
  "agents/updateAgent",
  async ({ agentId, agentData }, { rejectWithValue }) => {
    try {
      const data = await request(
        `${process.env.REACT_APP_API_URL}/agents/${agentId}/`,
        {
          method: "PATCH",
          body: JSON.stringify(agentData),
        }
      );
      return { agentId, ...data };
    } catch (error) {
      return rejectWithValue(error.error || "Failed to update agent");
    }
  }
);

// Create async thunk for deleting agent
export const deleteAgent = createAsyncThunk(
  "agents/deleteAgent",
  async (agentId, { rejectWithValue }) => {
    try {
      await request(`${process.env.REACT_APP_API_URL}/agents/${agentId}/`, {
        method: "DELETE",
      });
      return agentId;
    } catch (error) {
      return rejectWithValue(error.error || "Failed to delete agent");
    }
  }
);

// Create async thunk for searching agents
export const searchAgents = createAsyncThunk(
  "agents/searchAgents",
  async ({ searchTerm, page = 1 }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (searchTerm && searchTerm.trim()) {
        queryParams.append("search", searchTerm.trim());
      }
      queryParams.append("page", page);

      const data = await request(
        `${process.env.REACT_APP_API_URL}/agents/?${queryParams.toString()}`,
        { method: "GET" }
      );
      return { ...data, searchTerm, page };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to search agents");
    }
  }
);

// Create async thunk for fetching agent details
export const fetchAgentDetails = createAsyncThunk(
  "agents/fetchAgentDetails",
  async (agentId, { rejectWithValue }) => {
    try {
      const data = await request(
        `${process.env.REACT_APP_API_URL}/agents/${agentId}/`,
        { method: "GET" }
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.error || "Failed to fetch agent details");
    }
  }
);

// Define initial state
const initialState = {
  agents: [],
  agentsCount: 0,
  getAgentsStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
  getAgentsError: null,
  currentAgentPage: 1,
  shouldRefetchAgents: false,
  // Search state
  searchTerm: "",
  isSearching: false,
  searchAgentsStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
  searchAgentsError: null,
  createAgentStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
  createAgentError: null,
  updateAgentStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
  updateAgentError: null,
  deleteAgentStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
  deleteAgentError: null,
  // Agent details state
  agentDetails: null,
  fetchAgentDetailsStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
  fetchAgentDetailsError: null,
  // Image upload state
  isImageUploading: false,
  imageUploadingFields: {},
  imageUploadError: null,
  uploadedImages: {}, // Store multiple uploaded images by field name
};

const agentsSlice = createSlice({
  name: "agents",
  initialState,
  reducers: {
    setCurrentAgentPage: (state, action) => {
      state.currentAgentPage = action.payload;
    },
    clearCreateStatus: (state) => {
      state.createAgentStatus = "idle";
      state.createAgentError = null;
    },
    clearUpdateStatus: (state) => {
      state.updateAgentStatus = "idle";
      state.updateAgentError = null;
    },
    clearDeleteStatus: (state) => {
      state.deleteAgentStatus = "idle";
      state.deleteAgentError = null;
    },
    clearAgentDetails: (state) => {
      state.agentDetails = null;
      state.fetchAgentDetailsStatus = "idle";
      state.fetchAgentDetailsError = null;
    },
    clearRefetchFlag: (state) => {
      state.shouldRefetchAgents = false;
    },
    clearUploadedImages: (state) => {
      state.uploadedImages = {};
    },
    clearUploadedImage: (state, action) => {
      const fieldName = action.payload;
      if (fieldName && state.uploadedImages[fieldName]) {
        delete state.uploadedImages[fieldName];
      }
    },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    clearSearchResults: (state) => {
      state.searchTerm = "";
      state.isSearching = false;
      state.searchAgentsStatus = "idle";
      state.searchAgentsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch agents cases
      .addCase(fetchAgents.pending, (state) => {
        state.getAgentsStatus = "loading";
        state.getAgentsError = null;
      })
      .addCase(fetchAgents.fulfilled, (state, action) => {
        state.getAgentsStatus = "success";

        // Handle page redirection case
        if (action.payload.redirectedFromPage) {
          state.currentAgentPage = action.payload.actualPage;
        }

        // Handle both mock data (array) and real API response (paginated object)
        if (Array.isArray(action.payload)) {
          // Mock data - simulate server-side pagination
          const itemsPerPage = 10;
          const startIndex = (state.currentAgentPage - 1) * itemsPerPage;
          const endIndex = startIndex + itemsPerPage;

          state.agents = action.payload.slice(startIndex, endIndex);
          state.agentsCount = action.payload.length;
        } else {
          // Real API response with pagination
          state.agents = action.payload.results || [];
          state.agentsCount = action.payload.count || 0;
        }
      })
      .addCase(fetchAgents.rejected, (state, action) => {
        state.getAgentsStatus = "failed";
        state.getAgentsError = action.payload;
      })

      // Create agent cases
      .addCase(createAgent.pending, (state) => {
        state.createAgentStatus = "loading";
        state.createAgentError = null;
      })
      .addCase(createAgent.fulfilled, (state, action) => {
        state.createAgentStatus = "success";
        state.shouldRefetchAgents = true;
      })
      .addCase(createAgent.rejected, (state, action) => {
        state.createAgentStatus = "failed";
        state.createAgentError = action.payload;
      })

      // Update agent cases
      .addCase(updateAgent.pending, (state) => {
        state.updateAgentStatus = "loading";
        state.updateAgentError = null;
      })
      .addCase(updateAgent.fulfilled, (state, action) => {
        state.updateAgentStatus = "success";
        // Update the agent in the list if it exists
        const index = state.agents.findIndex(
          (agent) => agent.id === action.payload.agentId
        );
        if (index !== -1) {
          state.agents[index] = { ...state.agents[index], ...action.payload };
        }
        // Also update agentDetails if it matches the updated agent
        if (
          state.agentDetails &&
          state.agentDetails.id === action.payload.agentId
        ) {
          state.agentDetails = { ...state.agentDetails, ...action.payload };
        }
      })
      .addCase(updateAgent.rejected, (state, action) => {
        state.updateAgentStatus = "failed";
        state.updateAgentError = action.payload;
      })

      // Delete agent cases
      .addCase(deleteAgent.pending, (state) => {
        state.deleteAgentStatus = "loading";
        state.deleteAgentError = null;
      })
      .addCase(deleteAgent.fulfilled, (state, action) => {
        state.deleteAgentStatus = "success";
        // Remove the agent from the list
        state.agents = state.agents.filter(
          (agent) => agent.id !== action.payload
        );
        state.agentsCount = Math.max(0, state.agentsCount - 1);
      })
      .addCase(deleteAgent.rejected, (state, action) => {
        state.deleteAgentStatus = "failed";
        state.deleteAgentError = action.payload;
      })

      // Fetch agent details cases
      .addCase(fetchAgentDetails.pending, (state) => {
        state.fetchAgentDetailsStatus = "loading";
        state.fetchAgentDetailsError = null;
      })
      .addCase(fetchAgentDetails.fulfilled, (state, action) => {
        state.fetchAgentDetailsStatus = "success";
        state.agentDetails = action.payload;
      })
      .addCase(fetchAgentDetails.rejected, (state, action) => {
        state.fetchAgentDetailsStatus = "failed";
        state.fetchAgentDetailsError = action.payload;
      })

      // Upload Agent Image cases
      .addCase(uploadAgentImage.pending, (state, action) => {
        const { fieldName } = action.meta.arg;
        state.isImageUploading = true;
        state.imageUploadingFields[fieldName] = true;
        state.imageUploadError = null;
      })
      .addCase(uploadAgentImage.fulfilled, (state, action) => {
        const { response: imageResponse, fieldName } = action.payload;

        state.isImageUploading = false;
        state.imageUploadingFields[fieldName] = false;
        state.imageUploadError = null;

        // Extract the image data from the response (it's a single object, not an array)
        if (imageResponse && imageResponse.id && imageResponse.presigned_url) {
          state.uploadedImages[fieldName] = {
            id: imageResponse.id,
            url: imageResponse.presigned_url,
          };
        }
      })
      .addCase(uploadAgentImage.rejected, (state, action) => {
        const fieldName =
          action.payload?.fieldName || action.meta?.arg?.fieldName;

        state.isImageUploading = false;
        if (fieldName) {
          state.imageUploadingFields[fieldName] = false;
        }
        state.imageUploadError = action.payload?.error || action.payload;
      })

      // Search agents cases
      .addCase(searchAgents.pending, (state) => {
        state.searchAgentsStatus = "loading";
        state.searchAgentsError = null;
        state.isSearching = true;
      })
      .addCase(searchAgents.fulfilled, (state, action) => {
        state.searchAgentsStatus = "success";
        state.isSearching = false;
        state.searchTerm = action.payload.searchTerm;
        state.currentAgentPage = action.payload.page;

        // Update agents list with search results
        state.agents = action.payload.results || [];
        state.agentsCount = action.payload.count || 0;
      })
      .addCase(searchAgents.rejected, (state, action) => {
        state.searchAgentsStatus = "failed";
        state.searchAgentsError = action.payload;
        state.isSearching = false;
      });
  },
});

export const {
  setCurrentAgentPage,
  clearCreateStatus,
  clearUpdateStatus,
  clearDeleteStatus,
  clearAgentDetails,
  clearRefetchFlag,
  clearUploadedImages,
  clearUploadedImage,
  setSearchTerm,
  clearSearchResults,
} = agentsSlice.actions;

export default agentsSlice.reducer;
